import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { postWithDupCheck } from "@/lib/dupCheck";
import { reviewMagazzinoItems } from "@/components/MagazzinoReview";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Loader2, Receipt, Search } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const emptyForm = () => ({ numero: "", data: new Date().toISOString().slice(0, 10), fornitore_id: null, cantiere_id: null, descrizione: "", importo_stimato: 0 });

export const AggiungiDdtDialog = ({ open, onOpenChange, fornitori, cantieri, onSaved, onOpenFattura }) => {
  const [step, setStep] = useState("choose");
  useEffect(() => { if (open) setStep("choose"); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="aggiungi-ddt-dialog">
        <DialogHeader>
          <DialogTitle>Aggiungi DDT</DialogTitle>
          <DialogDescription>Scegli come vuoi procedere.</DialogDescription>
        </DialogHeader>
        {step === "choose" && (
          <div className="grid gap-3">
            <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => setStep("scan")} data-testid="aggiungi-ddt-scan-choice">
              <Camera className="h-5 w-5 mr-3 text-primary" />
              <span className="text-left"><b>Scansiona o carica un DDT</b><br /><span className="text-xs text-muted-foreground">Foto o PDF di una singola bolla, oppure inserimento a mano</span></span>
            </Button>
            <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => setStep("fattura")} data-testid="aggiungi-ddt-fattura-choice">
              <Receipt className="h-5 w-5 mr-3 text-primary" />
              <span className="text-left"><b>DDT di una fattura mensile</b><br /><span className="text-xs text-muted-foreground">Vedi, aggiungi o scollega i DDT di una fattura riepilogativa</span></span>
            </Button>
          </div>
        )}
        {step === "scan" && <ScanDdt fornitori={fornitori} cantieri={cantieri} onSaved={() => { onOpenChange(false); onSaved?.(); }} />}
        {step === "fattura" && <ScegliFattura onPick={(fid) => { onOpenChange(false); onOpenFattura(fid); }} />}
      </DialogContent>
    </Dialog>
  );
};

const ScanDdt = ({ fornitori, cantieri, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [extra, setExtra] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      const fd = new FormData();
      fd.append("file", isPdf ? file : await compressImage(file, { maxDim: 1600, quality: 0.85 }), file.name);
      const { data } = await api.post("/documenti/ocr", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const d = data.ddt || {};
      setForm({
        numero: d.numero || "", data: /^\d{4}-\d{2}-\d{2}$/.test(d.data || "") ? d.data : emptyForm().data,
        fornitore_id: d.fornitore_id || null, cantiere_id: d.cantiere_id || null,
        descrizione: d.descrizione || "", importo_stimato: Number(d.importo_stimato) || 0,
      });
      setExtra({
        magazzino_items: d.magazzino_items?.length ? d.magazzino_items : null,
        allegato_base64: data.original_base64 || null, allegato_mime: data.original_mime || null, allegato_filename: data.original_filename || null,
        auto_create_supplier: true, fornitore_nome: d.fornitore || "", fornitore_partita_iva: d.fornitore_partita_iva || "",
      });
      toast.success("Dati estratti. Controlla e salva.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scansione fallita, inserisci a mano"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      let magItems = extra.magazzino_items;
      if (magItems?.length) { magItems = await reviewMagazzinoItems(magItems); if (magItems === null) return; }
      const r = await postWithDupCheck("/ddt", { ...form, ...extra, magazzino_items: magItems, importo_stimato: Number(form.importo_stimato) || 0 });
      if (r) { toast.success(r.data?.overwritten ? "DDT sovrascritto" : "DDT salvato"); onSaved(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio DDT"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="aggiungi-ddt-file" />
      <Button variant="outline" className="w-full" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="aggiungi-ddt-scan-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />} Scansiona o carica (AI)
      </Button>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Numero</Label><Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="aggiungi-ddt-numero" /></div>
        <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="aggiungi-ddt-data" /></div>
        <div className="col-span-2"><Label>Fornitore</Label>
          <Select value={form.fornitore_id || "__none__"} onValueChange={(v) => setForm({ ...form, fornitore_id: v === "__none__" ? null : v })}>
            <SelectTrigger data-testid="aggiungi-ddt-fornitore"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{extra.fornitore_nome ? `Nuovo: ${extra.fornitore_nome}` : "— Non assegnato —"}</SelectItem>
              {fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Cantiere</Label>
          <Select value={form.cantiere_id || "__none__"} onValueChange={(v) => setForm({ ...form, cantiere_id: v === "__none__" ? null : v })}>
            <SelectTrigger data-testid="aggiungi-ddt-cantiere"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Da assegnare —</SelectItem>
              {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
        <div className="col-span-2"><Label>Importo stimato €</Label><Input type="number" step="0.01" value={form.importo_stimato} onChange={(e) => setForm({ ...form, importo_stimato: e.target.value })} data-testid="aggiungi-ddt-importo" /></div>
      </div>
      <Button className="w-full h-11" disabled={busy} onClick={save} data-testid="aggiungi-ddt-save">Salva DDT</Button>
    </div>
  );
};

const ScegliFattura = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = { tipo: "acquisto", limit: 20, ...(q.trim() ? { search: q.trim() } : {}) };
      api.get("/fatture", { params }).then(({ data }) => setList(data || [])).catch(() => setList([]));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" placeholder="Cerca fattura per fornitore, numero o importo" value={q} onChange={(e) => setQ(e.target.value)} data-testid="aggiungi-ddt-fattura-search" />
      </div>
      <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
        {list === null && <Loader2 className="h-4 w-4 animate-spin mx-auto my-4" />}
        {list?.length === 0 && <div className="p-4 text-sm text-center text-muted-foreground">Nessuna fattura trovata.</div>}
        {list?.map((f) => (
          <button key={f.id} type="button" onClick={() => onPick(f.id)} data-testid={`aggiungi-ddt-fattura-${f.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex justify-between gap-2">
            <span><b>n. {f.numero || "s.n."}</b> · {f.controparte} · {f.data}</span>
            <span className="text-xs tabular-nums">{fmt(f.totale)} · {(f.ddt_ids || []).length} DDT</span>
          </button>
        ))}
      </div>
    </div>
  );
};
